import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { useSelector } from "react-redux";
import {
  User,
  Clock,
  Coins,
  X,
  Activity,
  Briefcase,
  Search,
  Phone,
  Users,
  Gift,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ITEMS_PER_PAGE = 44;

const UserCardsDashboard = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("user");
  const [activeSection, setActiveSection] = useState("users");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [currentEmployeePage, setCurrentEmployeePage] = useState(1);
  const [userData, setUserData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserType, setCurrentUserType] = useState(null);
  const { token } = useSelector((state) => state.userSlice);
  const { userData: user } = useSelector((store) => store.userSlice);
  const admin = user?.user_type;
  const HISTORY_ITEMS_PER_PAGE = 40;

  // History pagination states
  const [currentUserHistoryPage, setCurrentUserHistoryPage] = useState(1);
  const [currentEmployeeHistoryPage, setCurrentEmployeeHistoryPage] =
    useState(1);
  const [currentEmployeeRedemptionPage, setCurrentEmployeeRedemptionPage] =
    useState(1);

  // History search and filter states
  const [userHistorySearch, setUserHistorySearch] = useState("");
  const [employeeHistorySearch, setEmployeeHistorySearch] = useState("");
  const [employeeRedemptionSearch, setEmployeeRedemptionSearch] = useState("");
  const [userHistoryDateFilter, setUserHistoryDateFilter] = useState("");
  const [employeeHistoryDateFilter, setEmployeeHistoryDateFilter] =
    useState("");
  const [userHistoryTypeFilter, setUserHistoryTypeFilter] = useState("all");
  const [employeeHistoryTypeFilter, setEmployeeHistoryTypeFilter] =
    useState("all");

  // Memoized filtered data to avoid recalculation on every render
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery) return userData;

    const query = userSearchQuery.toLowerCase();
    return userData.filter((user) => {
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.includes(userSearchQuery) ||
        user.city?.toLowerCase().includes(query) ||
        user.state?.toLowerCase().includes(query)
      );
    });
  }, [userData, userSearchQuery]);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearchQuery) return employeeData;

    const query = employeeSearchQuery.toLowerCase();
    return employeeData.filter((employee) => {
      return (
        employee.name?.toLowerCase().includes(query) ||
        employee.email?.toLowerCase().includes(query) ||
        employee.phone?.includes(employeeSearchQuery) ||
        employee.department?.toLowerCase().includes(query) ||
        employee.empId?.toLowerCase().includes(query)
      );
    });
  }, [employeeData, employeeSearchQuery]);

  // Paginated data
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentUserPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentUserPage]);

  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentEmployeePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredEmployees.slice(startIndex, endIndex);
  }, [filteredEmployees, currentEmployeePage]);

  const totalUserPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const totalEmployeePages = Math.ceil(
    filteredEmployees.length / ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (selectedUser) {
      if (admin !== "admin" && admin !== "manager") {
        setActiveTab("user");
      }
      setCurrentUserHistoryPage(1);
      setCurrentEmployeeHistoryPage(1);
      setUserHistorySearch("");
      setEmployeeHistorySearch("");
      setUserHistoryDateFilter("");
      setEmployeeHistoryDateFilter("");
      setUserHistoryTypeFilter("all");
      setEmployeeHistoryTypeFilter("all");
    }
  }, [selectedUser]);

  // Reset filter functions
  const resetUserHistoryFilters = useCallback(() => {
    setUserHistorySearch("");
    setUserHistoryDateFilter("");
    setUserHistoryTypeFilter("all");
    setCurrentUserHistoryPage(1);
  }, []);

  const resetEmployeeHistoryFilters = useCallback(() => {
    setEmployeeHistorySearch("");
    setEmployeeHistoryDateFilter("");
    setEmployeeHistoryTypeFilter("all");
    setCurrentEmployeeHistoryPage(1);
  }, []);

  const resetEmployeeRedemptionFilters = useCallback(() => {
    setEmployeeRedemptionSearch("");
    setCurrentEmployeeRedemptionPage(1);
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      setCurrentEmployeeRedemptionPage(1);
      setEmployeeRedemptionSearch("");
    }
  }, [selectedEmployee]);

  // Optimized data transformation functions
  const transformUserData = useCallback((users, transactionsByUser) => {
    return users.map((user) => {
      const userTransactions = transactionsByUser[user._id] || [];

      // Optimize history processing
      const existingHistory =
        user.history?.map((h) => ({
          id: h._id,
          activity: h.activity,
          coinsUsed: parseInt(h.coins) || 0,
          date: new Date(h.timestamp).toLocaleDateString(),
          time: new Date(h.timestamp).toLocaleTimeString(),
          employee: h.redeemBy,
          type: "history",
          source: "user_history",
          timestamp: new Date(h.timestamp),
        })) || [];

      const transactionHistory = userTransactions.map((transaction) => ({
        id: transaction._id,
        activity: transaction.description,
        coinsUsed: transaction.coins || 0,
        date: new Date(transaction.createdAt).toLocaleDateString(),
        time: new Date(transaction.createdAt).toLocaleTimeString(),
        employee: null,
        type: transaction.type,
        source: "transaction",
        timestamp: new Date(transaction.createdAt),
      }));

      // Sort only when needed
      const sortedExistingHistory = existingHistory.sort(
        (a, b) => b.timestamp - a.timestamp
      );
      const sortedTransactionHistory = transactionHistory.sort(
        (a, b) => b.timestamp - a.timestamp
      );

      return {
        id: user._id,
        name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        email: user.email || "",
        phone: user.phone_no || "",
        avatar:
          (user?.first_name?.charAt(0) || "") +
            (user?.last_name?.charAt(0) || "") || "NA",
        totalCoins: user.funingo_money || 0,
        status: user.premium && user.premium.length > 0 ? "premium" : "active",
        city: user.city || "",
        state: user.state || "",
        dob: user.dob,
        gender: user.gender,
        verified: user.verified,
        regDate: user.reg_date,
        bookedTickets: user.booked_tickets?.length || 0,
        shortId: user.short_id,
        userHistory: sortedTransactionHistory,
        employeeHistory: sortedExistingHistory,
      };
    });
  }, []);

  const transformEmployeeData = useCallback((employees) => {
    return employees.map((emp) => ({
      id: emp._id,
      name: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
      email: emp.email || "",
      phone: emp.phone_no || "",
      avatar:
        (emp?.first_name?.charAt(0) || "") +
          (emp?.last_name?.charAt(0) || "") || "NA",
      department: emp.user_type === "admin" ? "Administration" : "Employee",
      empId: emp.emp_id,
      totalRedeemed: 0,
      status: emp.verified ? "active" : "inactive",
      city: emp.city || "",
      state: emp.state || "",
      redemptionHistory: [],
    }));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setCurrentUserType(admin);

        // Fetch data in parallel
        const [userHistoryResponse, usersResponse] = await Promise.all([
          axios.get(`${apiUrl}/user/getalltransactions`, {
            headers: { token: token, "Content-Type": "application/json" },
          }),
          axios.get(`${apiUrl}/user/getallusers`, {
            headers: { token: token, "Content-Type": "application/json" },
          }),
        ]);

        const allData = usersResponse.data.users || [];
        const transactionData = userHistoryResponse.data.data || [];

        // Separate users and employees
        const users = allData.filter((item) => item.user_type === "customer");
        const employees = allData.filter((item) =>
          ["employee", "admin", "window_employee"].includes(item.user_type)
        );

        const transactionsByUser = {};
        transactionData.forEach((transaction) => {
          const userId = transaction.user;
          if (!transactionsByUser[userId]) {
            transactionsByUser[userId] = [];
          }
          transactionsByUser[userId].push(transaction);
        });
        const transformedUsers = transformUserData(users, transactionsByUser);
        const transformedEmployees = transformEmployeeData(employees);

        // Process employee relationships
        const employeeMap = {};
        transformedEmployees.forEach((emp) => {
          if (emp.empId) {
            employeeMap[emp.empId] = emp;
          }
        });

        transformedUsers.forEach((user) => {
          user.employeeHistory.forEach((activity) => {
            if (activity.employee && employeeMap[activity.employee]) {
              const employee = employeeMap[activity.employee];

              const isWindowRecharge =
                activity.activity &&
                (activity.activity.toLowerCase().includes("window recharge") ||
                  activity.activity
                    .toLowerCase()
                    .includes("complementary coins"));

              employee.redemptionHistory.push({
                id: activity.id,
                item: activity.activity,
                coinsRedeemed: activity.coinsUsed,
                forUser: user.name,
                userId: user.id,
                date: activity.date,
                time: activity.time,
                userPhone: user.phone,
                isWindowRecharge,
              });

              activity.employee = employee.name;
              activity.empId = employee.empId;
            }
          });
        });

        // Calculate totals for employees
        transformedEmployees.forEach((emp) => {
          emp.totalRedeemed = emp.redemptionHistory.reduce((total, item) => {
            return item.isWindowRecharge ? total : total + item.coinsRedeemed;
          }, 0);

          emp.totalWindowRecharge = emp.redemptionHistory.reduce(
            (total, item) => {
              return item.isWindowRecharge ? total + item.coinsRedeemed : total;
            },
            0
          );
        });

        setUserData(transformedUsers);
        setEmployeeData(transformedEmployees);
        setError(null);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, admin, transformUserData, transformEmployeeData]);

  // Filtered history data
  const filteredUserHistory = useMemo(() => {
    if (!selectedUser) return [];
    let filtered = selectedUser.userHistory;

    if (userHistorySearch) {
      const query = userHistorySearch.toLowerCase();
      filtered = filtered.filter((item) =>
        item.activity?.toLowerCase().includes(query)
      );
    }

    if (userHistoryDateFilter) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.timestamp).toISOString().split("T")[0];
        return itemDate === userHistoryDateFilter;
      });
    }

    if (userHistoryTypeFilter !== "all") {
      filtered = filtered.filter((item) => item.type === userHistoryTypeFilter);
    }

    return filtered;
  }, [
    selectedUser,
    userHistorySearch,
    userHistoryDateFilter,
    userHistoryTypeFilter,
  ]);

  const filteredEmployeeHistory = useMemo(() => {
    if (!selectedUser) return [];
    let filtered = selectedUser.employeeHistory;

    if (employeeHistorySearch) {
      const query = employeeHistorySearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.activity?.toLowerCase().includes(query) ||
          item.employee?.toLowerCase().includes(query)
      );
    }

    if (employeeHistoryDateFilter) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.timestamp).toISOString().split("T")[0];
        return itemDate === employeeHistoryDateFilter;
      });
    }

    if (employeeHistoryTypeFilter !== "all") {
      if (employeeHistoryTypeFilter === "window_recharge") {
        filtered = filtered.filter(
          (item) =>
            item.activity?.toLowerCase().includes("window recharge") ||
            item.activity?.toLowerCase().includes("complementary coins")
        );
      } else if (employeeHistoryTypeFilter === "redemption") {
        filtered = filtered.filter(
          (item) =>
            !item.activity?.toLowerCase().includes("window recharge") &&
            !item.activity?.toLowerCase().includes("complementary coins")
        );
      }
    }

    return filtered;
  }, [
    selectedUser,
    employeeHistorySearch,
    employeeHistoryDateFilter,
    employeeHistoryTypeFilter,
  ]);

  const filteredEmployeeRedemptions = useMemo(() => {
    if (!selectedEmployee) return [];
    let filtered = selectedEmployee.redemptionHistory;

    if (employeeRedemptionSearch) {
      const query = employeeRedemptionSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.item?.toLowerCase().includes(query) ||
          item.forUser?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [selectedEmployee, employeeRedemptionSearch]);

  // Paginated history data
  const paginatedUserHistory = useMemo(() => {
    const startIndex = (currentUserHistoryPage - 1) * HISTORY_ITEMS_PER_PAGE;
    const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
    return filteredUserHistory.slice(startIndex, endIndex);
  }, [filteredUserHistory, currentUserHistoryPage]);

  const paginatedEmployeeHistory = useMemo(() => {
    const startIndex =
      (currentEmployeeHistoryPage - 1) * HISTORY_ITEMS_PER_PAGE;
    const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
    return filteredEmployeeHistory.slice(startIndex, endIndex);
  }, [filteredEmployeeHistory, currentEmployeeHistoryPage]);

  const paginatedEmployeeRedemptions = useMemo(() => {
    const startIndex =
      (currentEmployeeRedemptionPage - 1) * HISTORY_ITEMS_PER_PAGE;
    const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
    return filteredEmployeeRedemptions.slice(startIndex, endIndex);
  }, [filteredEmployeeRedemptions, currentEmployeeRedemptionPage]);

  // Total pages calculation
  const totalUserHistoryPages = Math.ceil(
    filteredUserHistory.length / HISTORY_ITEMS_PER_PAGE
  );
  const totalEmployeeHistoryPages = Math.ceil(
    filteredEmployeeHistory.length / HISTORY_ITEMS_PER_PAGE
  );
  const totalEmployeeRedemptionPages = Math.ceil(
    filteredEmployeeRedemptions.length / HISTORY_ITEMS_PER_PAGE
  );

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentUserPage(1);
  }, [userSearchQuery]);

  useEffect(() => {
    setCurrentEmployeePage(1);
  }, [employeeSearchQuery]);

  const isAdmin = currentUserType === "admin" || "manager";

  // Memoized card components
  const UserCard = React.memo(({ user }) => (
    <div
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 cursor-pointer border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1"
      onClick={() => {
        setSelectedUser(user);
        setActiveTab("user");
      }}
    >
      <div className="flex items-center space-x-4">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
            user.status === "premium"
              ? "bg-gradient-to-r from-purple-500 to-pink-500"
              : "bg-gradient-to-r from-blue-500 to-indigo-500"
          }`}
        >
          {user.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-lg truncate">
            {user.name}
          </h3>
          <p className="text-gray-600 text-sm truncate">{user.email}</p>
          <div className="flex items-center space-x-1 text-gray-500 text-sm mt-1">
            <Phone size={12} />
            <span className="truncate">{user.phone}</span>
          </div>
          <p className="text-gray-500 text-xs truncate">
            {user.city}, {user.state}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="flex items-center space-x-1 text-amber-600">
            <Coins size={16} />
            <span className="font-bold">{user.totalCoins}</span>
          </div>
          <span
            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
              user.status === "premium"
                ? "bg-purple-100 text-purple-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {user.status}
          </span>
          {user.bookedTickets > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Tickets: {user.bookedTickets}
            </p>
          )}
        </div>
      </div>
    </div>
  ));

  const EmployeeCard = React.memo(({ employee }) => (
    <div
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 cursor-pointer border border-gray-100 hover:border-green-200 transform hover:-translate-y-1"
      onClick={() => setSelectedEmployee(employee)}
    >
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
          {employee.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-lg truncate">
            {employee.name}
          </h3>
          <p className="text-gray-600 text-sm truncate">{employee.email}</p>
          <div className="flex items-center space-x-1 text-gray-500 text-sm mt-1">
            <Phone size={12} />
            <span className="truncate">{employee.phone}</span>
          </div>
          <p className="text-teal-600 text-sm font-medium truncate">
            {employee.department}
          </p>
          {employee.empId && (
            <p className="text-xs text-gray-500 truncate">
              ID: {employee.empId}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="flex items-center space-x-1 text-green-600">
            <Gift size={16} />
            <span className="font-bold">{employee.totalRedeemed}</span>
          </div>
          <span
            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
              employee.status === "active"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {employee.status}
          </span>
        </div>
      </div>
    </div>
  ));

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-center space-x-2 mt-8">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} />
        </button>

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-4 py-2 rounded-lg font-medium ${
              page === currentPage
                ? "bg-blue-500 text-white"
                : "border border-gray-300 hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  // Memoized calculation functions
  const calculateTotal = useCallback((history) => {
    return history.reduce((total, item) => {
      if (item.type === "credit") return total;

      if (
        item.activity &&
        (item.activity.toLowerCase().includes("window recharge") ||
          item.activity.toLowerCase().includes("complementary coins"))
      ) {
        return total;
      }

      const coins = item.coinsUsed || item.coinsRedeemed || 0;
      return coins === 0 || isNaN(coins) ? total : total + coins;
    }, 0);
  }, []);

  const calculateWindowRechargeCoins = useCallback((history) => {
    return history.reduce((total, item) => {
      if (
        item.activity &&
        (item.activity.toLowerCase().includes("window recharge") ||
          item.activity.toLowerCase().includes("complementary coins"))
      ) {
        return total + (item.coinsUsed || item.coinsRedeemed || 0);
      }
      return total;
    }, 0);
  }, []);

  const calculateTotalCredits = useCallback((history) => {
    return history.reduce((total, item) => {
      return item.type === "credit" ? total + item.coinsUsed : total;
    }, 0);
  }, []);

  // Memoized history components
  const HistoryItem = React.memo(({ item, showEmployee = false }) => {
    const isCredit = item.type === "credit";
    const isTransaction = item.source === "transaction";
    const isWindowRecharge =
      item.activity &&
      (item.activity.toLowerCase().includes("window recharge unlimited") ||
        item.activity.toLowerCase().includes("window recharge") ||
        item.activity.toLowerCase().includes("complementary coins"));

    return (
      <div
        className={`rounded-lg p-4 border ${
          isTransaction
            ? isCredit
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
            : isWindowRecharge
            ? "bg-green-50 border-green-200"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">
              {item.activity}
            </h4>
            {isTransaction && (
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                  isCredit
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {isCredit ? "Credit" : "Debit"}
              </span>
            )}
            {isWindowRecharge && (
              <span className="inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 bg-green-100 text-green-800">
                Window Recharge
              </span>
            )}
          </div>
          <div
            className={`flex items-center space-x-1 font-bold flex-shrink-0 ${
              isTransaction
                ? isCredit
                  ? "text-green-600"
                  : "text-red-600"
                : isWindowRecharge
                ? "text-green-600"
                : "text-amber-600"
            }`}
          >
            <Coins size={14} />
            <span>
              {isTransaction
                ? isCredit
                  ? "+"
                  : "-"
                : isWindowRecharge
                ? "+"
                : "-"}
              {item.coinsUsed}
            </span>
          </div>
        </div>
        {showEmployee && item.employee && (
          <>
            <p className="text-sm text-blue-600 mb-1 truncate">
              By: {item.employee}
            </p>
            {item.empId && (
              <p className="text-sm text-blue-600 mb-1">Emp ID: {item.empId}</p>
            )}
          </>
        )}
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Clock size={12} />
          <span>
            {item.date} at {item.time}
          </span>
          {isTransaction && (
            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
              Transaction
            </span>
          )}
          {isWindowRecharge && (
            <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">
              Recharge
            </span>
          )}
        </div>
      </div>
    );
  });

  const RedemptionItem = React.memo(({ item }) => (
    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium text-gray-900 flex-1 min-w-0 truncate">
          {item.item}
        </h4>
        <div className="flex items-center space-x-1 text-green-600 font-bold flex-shrink-0">
          <Gift size={14} />
          <span>{item.coinsRedeemed}</span>
        </div>
      </div>
      <p className="text-sm text-blue-600 mb-1 truncate">For: {item.forUser}</p>
      <p className="text-sm text-gray-600 mb-1">Phone: {item.userPhone}</p>
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Clock size={12} />
        <span>
          {item.date} at {item.time}
        </span>
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <X size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">History</h1>
          <p className="text-gray-600">
            Manage users and employees with their activity history
          </p>
          <div className="mt-2 text-sm text-gray-500">
            Users: {userData.length}{" "}
            {isAdmin && `| Employees: ${employeeData.length}`}
            {" | Showing "}
            {!isAdmin || activeSection === "users"
              ? `${Math.min(paginatedUsers.length, ITEMS_PER_PAGE)} of ${
                  filteredUsers.length
                }`
              : `${Math.min(paginatedEmployees.length, ITEMS_PER_PAGE)} of ${
                  filteredEmployees.length
                }`}
          </div>
        </div>

        {/* Section Toggle - Only show if admin */}
        {isAdmin && (
          <div className="mb-6">
            <div className="flex bg-white rounded-lg p-1 shadow-md w-fit">
              <button
                onClick={() => setActiveSection("users")}
                className={`flex items-center space-x-2 px-6 py-3 rounded-md font-medium transition-all ${
                  activeSection === "users"
                    ? "bg-blue-500 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <User size={18} />
                <span>Users ({userData.length})</span>
              </button>
              <button
                onClick={() => setActiveSection("employees")}
                className={`flex items-center space-x-2 px-6 py-3 rounded-md font-medium transition-all ${
                  activeSection === "employees"
                    ? "bg-green-500 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Users size={18} />
                <span>Employees ({employeeData.length})</span>
              </button>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"></div>
            <input
              type="text"
              placeholder={
                activeSection === "users" || !isAdmin
                  ? "Search users by name, email, phone, or location..."
                  : "Search employees by name, email, phone, department, or ID..."
              }
              value={
                activeSection === "users" || !isAdmin
                  ? userSearchQuery
                  : employeeSearchQuery
              }
              onChange={(e) =>
                activeSection === "users" || !isAdmin
                  ? setUserSearchQuery(e.target.value)
                  : setEmployeeSearchQuery(e.target.value)
              }
              className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {!isAdmin || activeSection === "users"
            ? paginatedUsers.map((user) => (
                <UserCard key={user.id} user={user} />
              ))
            : paginatedEmployees.map((employee) => (
                <EmployeeCard key={employee.id} employee={employee} />
              ))}
        </div>

        {/* Pagination */}
        {!isAdmin || activeSection === "users" ? (
          <Pagination
            currentPage={currentUserPage}
            totalPages={totalUserPages}
            onPageChange={setCurrentUserPage}
          />
        ) : (
          <Pagination
            currentPage={currentEmployeePage}
            totalPages={totalEmployeePages}
            onPageChange={setCurrentEmployeePage}
          />
        )}

        {/* No Results Message */}
        {((!isAdmin || activeSection === "users") &&
          filteredUsers.length === 0 &&
          userSearchQuery) ||
        (isAdmin &&
          activeSection === "employees" &&
          filteredEmployees.length === 0 &&
          employeeSearchQuery) ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No results found
            </h3>
            <p className="text-gray-600">Try adjusting your search terms</p>
          </div>
        ) : null}

        {/* User Popover Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-white font-bold text-xl">
                      {selectedUser.avatar}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        {selectedUser.name}
                      </h2>
                      <p className="text-blue-100">{selectedUser.email}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        <div className="flex items-center space-x-1">
                          <Phone size={14} />
                          <span>{selectedUser.phone}</span>
                        </div>
                        <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
                          {selectedUser.city}, {selectedUser.state}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {/* User Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Coins size={20} />
                      <span className="font-medium">Total Coins</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">
                      {selectedUser.totalCoins}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Activity size={20} />
                      <span className="font-medium">Status</span>
                    </div>
                    <p className="text-lg font-bold mt-1 capitalize">
                      {selectedUser.status}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <User size={20} />
                      <span className="font-medium">Tickets</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">
                      {selectedUser.bookedTickets}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Gift size={20} />
                      <span className="font-medium">Short ID</span>
                    </div>
                    <p className="text-lg font-bold mt-1">
                      {selectedUser.shortId || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Additional User Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">
                      Personal Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium">Date of Birth:</span>{" "}
                        {selectedUser.dob || "Not provided"}
                      </p>
                      <p>
                        <span className="font-medium">Gender:</span>{" "}
                        {selectedUser.gender || "Not provided"}
                      </p>
                      <p>
                        <span className="font-medium">Verified:</span>{" "}
                        {selectedUser.verified ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">
                      Account Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium">Registration Date:</span>{" "}
                        {selectedUser.regDate
                          ? new Date(selectedUser.regDate).toLocaleDateString()
                          : "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Premium Status:</span>{" "}
                        {selectedUser.status === "premium"
                          ? "Active"
                          : "Standard"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="mb-6">
                  <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
                    <button
                      onClick={() => setActiveTab("user")}
                      className={`px-4 py-2 rounded-md font-medium transition-all ${
                        activeTab === "user"
                          ? "bg-white text-blue-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Transaction History ({selectedUser.userHistory.length})
                    </button>
                    {(admin === "admin" || admin === "manager") && (
                      <button
                        onClick={() => setActiveTab("employee")}
                        className={`px-4 py-2 rounded-md font-medium transition-all ${
                          activeTab === "employee"
                            ? "bg-white text-green-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Employee History ({selectedUser.employeeHistory.length})
                      </button>
                    )}
                  </div>
                </div>

                {/* History Content */}
                {activeTab === "user" && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Transaction History
                      </h3>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1 text-green-600">
                          <span className="font-medium">Credits:</span>
                          <span className="font-bold">
                            +{calculateTotalCredits(selectedUser.userHistory)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-red-600">
                          <span className="font-medium">Debits:</span>
                          <span className="font-bold">
                            -{calculateTotal(selectedUser.userHistory)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 space-y-3">
                      <div className="flex flex-wrap gap-3">
                        <div className="flex-1 min-w-64">
                          <input
                            type="text"
                            placeholder="Search transactions..."
                            value={userHistorySearch}
                            onChange={(e) =>
                              setUserHistorySearch(e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <input
                          type="date"
                          value={userHistoryDateFilter}
                          onChange={(e) =>
                            setUserHistoryDateFilter(e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <select
                          value={userHistoryTypeFilter}
                          onChange={(e) =>
                            setUserHistoryTypeFilter(e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="all">All Types</option>
                          <option value="credit">Credit</option>
                          <option value="debit">Debit</option>
                        </select>
                        <button
                          onClick={resetUserHistoryFilters}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        Showing {paginatedUserHistory.length} of{" "}
                        {filteredUserHistory.length} transactions
                      </div>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {paginatedUserHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Activity
                            size={48}
                            className="mx-auto mb-4 opacity-50"
                          />
                          <p>
                            {filteredUserHistory.length === 0 &&
                            (userHistorySearch ||
                              userHistoryDateFilter ||
                              userHistoryTypeFilter !== "all")
                              ? "No transactions match your filters"
                              : "No transaction history available"}
                          </p>
                        </div>
                      ) : (
                        paginatedUserHistory.map((item) => (
                          <HistoryItem key={item.id} item={item} />
                        ))
                      )}
                    </div>

                    {totalUserHistoryPages > 1 && (
                      <Pagination
                        currentPage={currentUserHistoryPage}
                        totalPages={totalUserHistoryPages}
                        onPageChange={setCurrentUserHistoryPage}
                      />
                    )}
                  </div>
                )}

                {activeTab === "employee" &&
                  (admin === "admin" || admin === "manager") && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Employee Redemptions
                        </h3>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1 text-green-600">
                            <span className="font-medium">
                              Window Recharge:
                            </span>
                            <span className="font-bold">
                              +
                              {calculateWindowRechargeCoins(
                                selectedUser.employeeHistory
                              )}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 text-amber-600">
                            <span className="font-medium">Redeemed:</span>
                            <span className="font-bold">
                              -{calculateTotal(selectedUser.employeeHistory)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4 space-y-3">
                        <div className="flex flex-wrap gap-3">
                          <div className="flex-1 min-w-64">
                            <input
                              type="text"
                              placeholder="Search by activity or employee..."
                              value={employeeHistorySearch}
                              onChange={(e) =>
                                setEmployeeHistorySearch(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <input
                            type="date"
                            value={employeeHistoryDateFilter}
                            onChange={(e) =>
                              setEmployeeHistoryDateFilter(e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                          <select
                            value={employeeHistoryTypeFilter}
                            onChange={(e) =>
                              setEmployeeHistoryTypeFilter(e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          >
                            <option value="all">All Types</option>
                            <option value="window_recharge">
                              Window Recharge
                            </option>
                            <option value="redemption">Redemptions</option>
                          </select>
                          <button
                            onClick={resetEmployeeHistoryFilters}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
                          >
                            Reset
                          </button>
                        </div>
                        <div className="text-xs text-gray-500">
                          Showing {paginatedEmployeeHistory.length} of{" "}
                          {filteredEmployeeHistory.length} activities
                        </div>
                      </div>

                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {paginatedEmployeeHistory.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <Briefcase
                              size={48}
                              className="mx-auto mb-4 opacity-50"
                            />
                            <p>
                              {filteredEmployeeHistory.length === 0 &&
                              (employeeHistorySearch ||
                                employeeHistoryDateFilter ||
                                employeeHistoryTypeFilter !== "all")
                                ? "No activities match your filters"
                                : "No employee redemption history available"}
                            </p>
                          </div>
                        ) : (
                          paginatedEmployeeHistory.map((item) => (
                            <HistoryItem
                              key={item.id}
                              item={item}
                              showEmployee={true}
                            />
                          ))
                        )}
                      </div>

                      {totalEmployeeHistoryPages > 1 && (
                        <Pagination
                          currentPage={currentEmployeeHistoryPage}
                          totalPages={totalEmployeeHistoryPages}
                          onPageChange={setCurrentEmployeeHistoryPage}
                        />
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Employee Popover Modal */}
        {selectedEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-white font-bold text-xl">
                      {selectedEmployee.avatar}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        {selectedEmployee.name}
                      </h2>
                      <p className="text-green-100">{selectedEmployee.email}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        <div className="flex items-center space-x-1">
                          <Phone size={14} />
                          <span>{selectedEmployee.phone}</span>
                        </div>
                        <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
                          {selectedEmployee.department}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEmployee(null)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {/* Employee Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Gift size={20} />
                      <span className="font-medium">Total Redeemed</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">
                      {selectedEmployee.totalRedeemed}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Coins size={20} />
                      <span className="font-medium">Window Recharge</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">
                      {selectedEmployee.totalWindowRecharge || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Activity size={20} />
                      <span className="font-medium">Status</span>
                    </div>
                    <p className="text-lg font-bold mt-1 capitalize">
                      {selectedEmployee.status}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-4 text-white">
                    <div className="flex items-center space-x-2">
                      <Briefcase size={20} />
                      <span className="font-medium">Employee ID</span>
                    </div>
                    <p className="text-lg font-bold mt-1">
                      {selectedEmployee.empId || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Additional Employee Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">
                    Employee Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <p>
                      <span className="font-medium">Department:</span>{" "}
                      {selectedEmployee.department}
                    </p>
                    <p>
                      <span className="font-medium">Location:</span>{" "}
                      {selectedEmployee.city}, {selectedEmployee.state}
                    </p>
                    <p>
                      <span className="font-medium">Total Redemptions:</span>{" "}
                      {selectedEmployee.redemptionHistory.length}
                    </p>
                    <p>
                      <span className="font-medium">Status:</span>{" "}
                      {selectedEmployee.status}
                    </p>
                  </div>
                </div>

                {/* Redemption History */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Redemption History (
                    {selectedEmployee.redemptionHistory.length})
                  </h3>

                  <div className="mb-4">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        placeholder="Search redemptions by item or user..."
                        value={employeeRedemptionSearch}
                        onChange={(e) =>
                          setEmployeeRedemptionSearch(e.target.value)
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      <button
                        onClick={resetEmployeeRedemptionFilters}
                        className="px-3 py-2 bg-red-100 hover:bg-green-200 text-white-700 rounded-md transition-colors text-sm font-medium"
                      >
                        Reset
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      Showing {paginatedEmployeeRedemptions.length} of{" "}
                      {filteredEmployeeRedemptions.length} redemptions
                    </div>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {paginatedEmployeeRedemptions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Gift size={48} className="mx-auto mb-4 opacity-50" />
                        <p>
                          {filteredEmployeeRedemptions.length === 0 &&
                          employeeRedemptionSearch
                            ? "No redemptions match your search"
                            : "No redemption history available"}
                        </p>
                      </div>
                    ) : (
                      paginatedEmployeeRedemptions.map((item) => (
                        <RedemptionItem key={item.id} item={item} />
                      ))
                    )}
                  </div>

                  {totalEmployeeRedemptionPages > 1 && (
                    <Pagination
                      currentPage={currentEmployeeRedemptionPage}
                      totalPages={totalEmployeeRedemptionPages}
                      onPageChange={setCurrentEmployeeRedemptionPage}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCardsDashboard;
